import { Injectable } from '@nestjs/common';
import { createHash, randomUUID } from 'crypto';
import { Request } from 'express';
import { StoreContextService } from '../tenancy/store-context.service';
import { AuditWriterService } from './audit-writer.service';

export interface ActivityEventInput {
  eventName?: unknown;
  route?: unknown;
  targetId?: unknown;
  props?: unknown;
  occurredAt?: unknown;
  sessionId?: unknown;
  anonymousId?: unknown;
  app?: unknown;
}

export interface ActivityBatchInput {
  events?: ActivityEventInput[];
  sessionId?: unknown;
  anonymousId?: unknown;
  app?: unknown;
}

/** Per request. The client flushes well below this; anything above is abuse or a bug. */
const MAX_EVENTS_PER_BATCH = 50;
const MAX_PROP_KEYS = 12;
const MAX_STRING_LENGTH = 200;

/**
 * Event names the storefront and dashboards are allowed to send.
 *
 * An allowlist rather than free text: it keeps a compromised or buggy client
 * from filling the table with unbounded distinct names, and it means the admin
 * event filter can offer a real list instead of whatever happens to be in the
 * data.
 */
const ALLOWED_EVENTS = new Set([
  'page.view',
  'route.change',
  'product.view',
  'product.image.zoom',
  'search.submit',
  'search.result.click',
  'category.view',
  'filter.apply',
  'sort.change',
  'cart.add',
  'cart.remove',
  'cart.quantity.change',
  'cart.view',
  'wishlist.add',
  'wishlist.remove',
  'checkout.start',
  'checkout.step',
  'checkout.address.submit',
  'checkout.payment.select',
  'checkout.complete',
  'checkout.abandon',
  'order.view',
  'auth.login.submit',
  'auth.register.submit',
  'banner.click',
  'nav.click',
  'cta.click',
  'error.shown',
]);

@Injectable()
export class ActivityService {
  constructor(
    private readonly writer: AuditWriterService,
    private readonly storeContext?: StoreContextService
  ) {}

  record(body: ActivityBatchInput, request: Request): { accepted: number } {
    const events = Array.isArray(body?.events) ? body.events.slice(0, MAX_EVENTS_PER_BATCH) : [];
    if (!events.length) return { accepted: 0 };

    const receivedAt = new Date();
    const userAgent = this.header(request, 'user-agent');
    const ipHash = this.hashIp(request.ip);
    const referrer = this.header(request, 'referer');
    const batchSession = this.str(body?.sessionId);
    const batchAnon = this.str(body?.anonymousId);
    const app = this.str(body?.app) ?? 'web';

    let accepted = 0;

    for (const event of events) {
      const eventName = this.str(event?.eventName);
      if (!eventName || !ALLOWED_EVENTS.has(eventName)) continue;

      this.writer.enqueue('activityLog', {
        id: randomUUID(),
        organizationId: this.storeContext?.organizationId() ?? null,
        storeId: this.storeContext?.storeId() ?? null,
        // Taken from the authenticated request, never from the body — a client
        // must not be able to attribute its activity to another user.
        userId: (request as { user?: { id?: string } }).user?.id ?? null,
        anonymousId: this.str(event?.anonymousId) ?? batchAnon ?? null,
        sessionId: this.str(event?.sessionId) ?? batchSession ?? null,
        app: this.str(event?.app) ?? app,
        eventName,
        route: this.str(event?.route) ?? null,
        targetId: this.str(event?.targetId) ?? null,
        props: this.sanitiseProps(event?.props),
        occurredAt: this.clientDate(event?.occurredAt, receivedAt),
        receivedAt,
        userAgent: userAgent ?? null,
        ipHash,
        referrer: referrer ?? null,
      });

      accepted += 1;
    }

    return { accepted };
  }

  /**
   * Primitives only, bounded in count and length.
   *
   * This is the privacy boundary the plan committed to: no form values, no
   * keystrokes, no nested objects that could smuggle a payload through. A
   * client can only ever say short, flat, factual things here.
   */
  private sanitiseProps(props: unknown): Record<string, string | number | boolean> | undefined {
    if (!props || typeof props !== 'object' || Array.isArray(props)) return undefined;

    const out: Record<string, string | number | boolean> = {};

    for (const [key, value] of Object.entries(props as Record<string, unknown>)) {
      if (Object.keys(out).length >= MAX_PROP_KEYS) break;
      if (!/^[a-zA-Z][a-zA-Z0-9_]{0,31}$/.test(key)) continue;

      if (typeof value === 'string') out[key] = value.slice(0, MAX_STRING_LENGTH);
      else if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
      else if (typeof value === 'boolean') out[key] = value;
    }

    return Object.keys(out).length ? out : undefined;
  }

  /**
   * Client clocks are wrong and forgeable, so an implausible one is replaced
   * rather than trusted. Anything that must be chronologically sound reads
   * receivedAt instead.
   */
  private clientDate(value: unknown, receivedAt: Date): Date {
    if (typeof value !== 'number' && typeof value !== 'string') return receivedAt;

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return receivedAt;

    const skewMs = Math.abs(parsed.getTime() - receivedAt.getTime());
    return skewMs > 24 * 60 * 60 * 1000 ? receivedAt : parsed;
  }

  /** Hashed, never stored raw — activity is analytics, not security forensics. */
  private hashIp(ip: string | undefined): string | null {
    if (!ip) return null;

    const salt = process.env.ACTIVITY_IP_SALT ?? 'shopeee-activity';
    return createHash('sha256').update(`${salt}:${ip}`).digest('hex').slice(0, 32);
  }

  private header(request: Request, name: string): string | undefined {
    const value = request.headers?.[name];
    const single = Array.isArray(value) ? value[0] : value;
    return single?.slice(0, MAX_STRING_LENGTH);
  }

  private str(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim()
      ? value.trim().slice(0, MAX_STRING_LENGTH)
      : undefined;
  }

  /** Exposed so the admin event filter can offer the real list. */
  static allowedEvents(): string[] {
    return [...ALLOWED_EVENTS].sort();
  }
}
